import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Table,
  Badge,
  Nav,
  Tab,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import budgetService from '../../../services/budgetService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import '../../../styles/react-select.css';

// Registrar componentes de ECharts
echarts.use([
  LineChart,
  BarChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  CanvasRenderer,
]);

const FontAwesomeIcon = () => null;

// Colores por tipo de producto
const productColors = {
  BRAND: '#e63757',
  CORPORATIVO: '#2c7be5',
  EQUIPOS: '#00d97e',
  HOGAR: '#f5803e',
  POSTPAGO: '#6b5eae',
  PREPAGO: '#27bcfd',
  OTRO: '#95aac9',
};

// Colores por plataforma
const platformColors = {
  META: '#1877f2',
  GOOGLE: '#ea4335',
  TIKTOK: '#000000',
  LINKEDIN: '#0A66C2',
  OTRO: '#95aac9',
};

// Formatters
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num?.toLocaleString() || '0';
};

const formatCurrency = (num) => {
  return '$' + (num || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPercent = (num) => {
  return (num || 0).toFixed(2) + '%';
};

// Componente de Tab de Producto
const ProductTab = ({ product, isActive, onClick, data }) => {
  const productData = data?.find(p => p.productType === product);
  const color = productColors[product];
  
  return (
    <div 
      className={`text-center p-3 rounded cursor-pointer ${isActive ? 'shadow' : ''}`}
      style={{ 
        backgroundColor: isActive ? color : '#f8f9fa',
        color: isActive ? 'white' : '#333',
        cursor: 'pointer',
        transition: 'all 0.2s',
        minWidth: '120px',
      }}
      onClick={onClick}
    >
      <div className="fw-bold" style={{ fontSize: '14px' }}>{product}</div>
      {productData && (
        <div style={{ fontSize: '11px', opacity: 0.9 }}>
          {formatCurrency(productData.cost)}
        </div>
      )}
    </div>
  );
};

// Componente KPI Card
const KPICard = ({ title, value, subtitle, color = '#2c7be5', percentChange }) => (
  <Card className="h-100 shadow-sm border-0">
    <Card.Body className="py-2 px-3">
      <p className="text-500 mb-0 text-uppercase" style={{ fontSize: '10px' }}>{title}</p>
      <h4 className="mb-0 fw-bold" style={{ color, fontSize: '18px' }}>{value}</h4>
      {subtitle && <small className="text-muted" style={{ fontSize: '10px' }}>{subtitle}</small>}
      {percentChange !== undefined && (
        <div style={{ fontSize: '10px' }}>
          <Badge bg={percentChange >= 0 ? 'success' : 'danger'} className="mt-1">
            {percentChange >= 0 ? '+' : ''}{percentChange}%
          </Badge>
        </div>
      )}
    </Card.Body>
  </Card>
);


// Métricas disponibles para el gráfico evolutivo
const METRIC_OPTIONS = [
  { key: 'cost', label: 'Inversión', isCurrency: true },
  { key: 'reach', label: 'Alcance', isCurrency: false },
  { key: 'clicks', label: 'Clics', isCurrency: false },
  { key: 'linkClicks', label: 'Link Clics', isCurrency: false },
  { key: 'impressions', label: 'Impresiones', isCurrency: false },
  { key: 'videoViews', label: 'Video Views', isCurrency: false },
  { key: 'video100', label: 'Views 100%', isCurrency: false },
  { key: 'leads', label: 'Conversiones', isCurrency: false },
];

// Gráfico Evolutivo Principal con selectores
const EvolutiveChart = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState('impressions');
  const [selectedKPI, setSelectedKPI] = useState('cost');
  
  const metricConfig = METRIC_OPTIONS.find(m => m.key === selectedMetric) || METRIC_OPTIONS[4];
  const kpiConfig = METRIC_OPTIONS.find(m => m.key === selectedKPI) || METRIC_OPTIONS[0];

  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params) => {
          let result = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach(p => {
            const config = p.seriesIndex === 0 ? metricConfig : kpiConfig;
            const value = config.isCurrency ? formatCurrency(p.value) : formatNumber(p.value);
            result += `${p.marker} ${p.seriesName}: <strong>${value}</strong><br/>`;
          });
          return result;
        }
      },
      legend: { 
        data: [metricConfig.label, kpiConfig.label], 
        top: 0,
        itemGap: 20,
      },
      grid: { left: 60, right: 60, bottom: 50, top: 40 },
      xAxis: {
        type: 'category',
        data: data.map(d => {
          const dateStr = String(d.dateKey);
          return `${dateStr.slice(4,6)}/${dateStr.slice(6,8)}`;
        }),
        axisLabel: { fontSize: 10, rotate: 45 },
      },
      yAxis: [
        { 
          type: 'value', 
          name: metricConfig.label,
          nameTextStyle: { fontSize: 10 },
          axisLabel: { 
            formatter: v => metricConfig.isCurrency ? '$' + formatNumber(v) : formatNumber(v),
            fontSize: 10 
          }, 
          splitLine: { show: true, lineStyle: { type: 'dashed', color: '#eee' } } 
        },
        { 
          type: 'value', 
          name: kpiConfig.label,
          nameTextStyle: { fontSize: 10 },
          position: 'right', 
          axisLabel: { 
            formatter: v => kpiConfig.isCurrency ? '$' + formatNumber(v) : formatNumber(v),
            fontSize: 10 
          },
          splitLine: { show: false }
        },
      ],
      series: [
        {
          name: metricConfig.label,
          type: 'bar',
          data: data.map(d => d[selectedMetric] || 0),
          itemStyle: { color: '#f5803e' },
          barMaxWidth: 40,
          label: {
            show: data.length <= 20,
            position: 'top',
            formatter: (p) => metricConfig.isCurrency ? '$' + formatNumber(p.value) : formatNumber(p.value),
            fontSize: 8,
            color: '#333',
          }
        },
        {
          name: kpiConfig.label,
          type: 'line',
          yAxisIndex: 1,
          data: data.map(d => d[selectedKPI] || 0),
          lineStyle: { color: '#333', width: 2 },
          itemStyle: { color: '#333' },
          symbol: 'circle',
          symbolSize: 5,
          label: {
            show: data.length <= 15,
            position: 'top',
            formatter: (p) => kpiConfig.isCurrency ? '$' + formatNumber(p.value) : formatNumber(p.value),
            fontSize: 8,
            color: '#333',
          }
        },
      ],
    };
  }, [data, selectedMetric, selectedKPI, metricConfig, kpiConfig]);

  if (!data || data.length === 0) {
    return <div className="text-center text-muted py-4">Sin datos</div>;
  }

  return (
    <Card className="shadow-sm border-0">
      <Card.Header className="bg-white border-bottom py-2">
        <h6 className="mb-0">
          📈 EVOLUTIVO
        </h6>
      </Card.Header>
      <Card.Body>
        <Row className="g-0">
          {/* Selector de Parámetro/Métrica (izquierda) */}
          <Col xs={2} className="pe-2">
            <div className="mb-2 pb-1 border-bottom">
              <small className="text-muted fw-bold">Parámetro Métricas</small>
            </div>
            {METRIC_OPTIONS.map(metric => (
              <button
                key={metric.key}
                className="btn btn-sm w-100 mb-1 text-start"
                style={{ 
                  backgroundColor: selectedMetric === metric.key ? '#f5803e' : '#f8f9fa',
                  color: selectedMetric === metric.key ? 'white' : '#333',
                  border: 'none',
                  fontSize: '11px',
                  padding: '5px 8px',
                }}
                onClick={() => setSelectedMetric(metric.key)}
              >
                {metric.label}
              </button>
            ))}
          </Col>
          
          {/* Gráfico (centro) */}
          <Col xs={8} className="px-2">
            <div className="text-center mb-1">
              <small>
                <span style={{ color: '#f5803e' }}>● {metricConfig.label}</span>
                {' '}
                <span style={{ color: '#333' }}>● {kpiConfig.label}</span>
              </small>
            </div>
            <ReactEChartsCore echarts={echarts} option={options} style={{ height: 320 }} />
          </Col>
          
          {/* Selector de KPI (derecha) */}
          <Col xs={2} className="ps-2">
            <div className="mb-2 pb-1 border-bottom">
              <small className="text-muted fw-bold">KPI (líneas)</small>
            </div>
            {METRIC_OPTIONS.map(metric => (
              <button
                key={metric.key}
                className="btn btn-sm w-100 mb-1 text-start"
                style={{ 
                  backgroundColor: selectedKPI === metric.key ? '#333' : '#f8f9fa',
                  color: selectedKPI === metric.key ? 'white' : '#333',
                  border: 'none',
                  fontSize: '11px',
                  padding: '5px 8px',
                }}
                onClick={() => setSelectedKPI(metric.key)}
              >
                {metric.label}
              </button>
            ))}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

// Gráfico de doble eje (barras + línea con área)
const DualAxisChart = ({ data, title, barKey, lineKey, barLabel, lineLabel, barColor, lineColor, icon }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
      },
      legend: { 
        data: [barLabel, lineLabel], 
        top: 5,
        textStyle: { fontSize: 11 }
      },
      grid: { left: 50, right: 50, bottom: 40, top: 45 },
      xAxis: {
        type: 'category',
        data: data.map(d => {
          const dateStr = String(d.dateKey);
          return `${dateStr.slice(4,6)}/${dateStr.slice(6,8)}`;
        }),
        axisLabel: { fontSize: 9, rotate: 45 },
      },
      yAxis: [
        { 
          type: 'value', 
          name: barLabel,
          nameTextStyle: { fontSize: 9 },
          axisLabel: { formatter: v => formatNumber(v), fontSize: 9 }, 
          splitLine: { show: true, lineStyle: { type: 'dashed', color: '#eee' } } 
        },
        { 
          type: 'value', 
          name: lineLabel,
          nameTextStyle: { fontSize: 9 },
          position: 'right', 
          axisLabel: { formatter: v => lineKey.includes('ctr') ? v.toFixed(2) + '%' : '$' + formatNumber(v), fontSize: 9 },
          splitLine: { show: false }
        },
      ],
      series: [
        {
          name: barLabel,
          type: 'bar',
          data: data.map(d => d[barKey] || 0),
          itemStyle: { color: barColor },
          barMaxWidth: 25,
        },
        {
          name: lineLabel,
          type: 'line',
          yAxisIndex: 1,
          data: data.map(d => d[lineKey] || 0),
          lineStyle: { color: lineColor, width: 2 },
          itemStyle: { color: lineColor },
          symbol: 'circle',
          symbolSize: 4,
          areaStyle: { color: lineColor, opacity: 0.1 },
        },
      ],
    };
  }, [data, barKey, lineKey, barLabel, lineLabel, barColor, lineColor]);

  if (!data || data.length === 0) {
    return <div className="text-center text-muted py-4">Sin datos</div>;
  }

  return (
    <Card className="shadow-sm border-0 h-100">
      <Card.Header className="bg-white border-bottom py-2">
        <h6 className="mb-0" style={{ fontSize: '13px' }}>
          {title}
        </h6>
      </Card.Header>
      <Card.Body className="p-2">
        <ReactEChartsCore echarts={echarts} option={options} style={{ height: 220 }} />
      </Card.Body>
    </Card>
  );
};

// Gráfico de Tendencia General (3 series)
const TrendChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    return {
      tooltip: { trigger: 'axis' },
      legend: { 
        data: ['Impresiones', 'Clicks', 'Gasto'], 
        top: 5,
        textStyle: { fontSize: 11 }
      },
      grid: { left: 50, right: 50, bottom: 40, top: 45 },
      xAxis: {
        type: 'category',
        data: data.map(d => {
          const dateStr = String(d.dateKey);
          return `${dateStr.slice(4,6)}-${dateStr.slice(6,8)}`;
        }),
        axisLabel: { fontSize: 9, rotate: 45 },
      },
      yAxis: [
        { 
          type: 'value', 
          name: 'Impresiones/Clicks',
          nameTextStyle: { fontSize: 9 },
          axisLabel: { formatter: v => formatNumber(v), fontSize: 9 }, 
          splitLine: { show: true, lineStyle: { type: 'dashed', color: '#eee' } } 
        },
        { 
          type: 'value', 
          name: 'Gasto ($)',
          nameTextStyle: { fontSize: 9 },
          position: 'right', 
          axisLabel: { formatter: v => '$' + formatNumber(v), fontSize: 9 },
          splitLine: { show: false }
        },
      ],
      series: [
        {
          name: 'Impresiones',
          type: 'line',
          data: data.map(d => d.impressions || 0),
          lineStyle: { color: '#5b9bd5', width: 2 },
          itemStyle: { color: '#5b9bd5' },
          symbol: 'circle',
          symbolSize: 3,
        },
        {
          name: 'Clicks',
          type: 'line',
          data: data.map(d => d.clicks || 0),
          lineStyle: { color: '#70ad47', width: 2 },
          itemStyle: { color: '#70ad47' },
          symbol: 'circle',
          symbolSize: 3,
          areaStyle: { color: '#70ad47', opacity: 0.2 },
        },
        {
          name: 'Gasto',
          type: 'bar',
          yAxisIndex: 1,
          data: data.map(d => d.cost || 0),
          itemStyle: { color: '#f5803e', opacity: 0.7 },
          barMaxWidth: 20,
        },
      ],
    };
  }, [data]);

  if (!data || data.length === 0) {
    return <div className="text-center text-muted py-4">Sin datos</div>;
  }

  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 280 }} />;
};

// Gráfico de Costo por Día
const CostPerDayChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    return {
      tooltip: { 
        trigger: 'axis',
        formatter: (params) => {
          const p = params[0];
          return `${p.axisValue}<br/>${p.marker} Costo: <strong>${formatCurrency(p.value)}</strong>`;
        }
      },
      grid: { left: 60, right: 20, bottom: 40, top: 30 },
      xAxis: {
        type: 'category',
        data: data.map(d => {
          const dateStr = String(d.dateKey);
          return `${dateStr.slice(4,6)}/${dateStr.slice(6,8)}`;
        }),
        axisLabel: { fontSize: 9, rotate: 45 },
      },
      yAxis: { 
        type: 'value',
        axisLabel: { formatter: v => '$' + formatNumber(v), fontSize: 9 },
        splitLine: { show: true, lineStyle: { type: 'dashed', color: '#eee' } }
      },
      series: [{
        type: 'line',
        data: data.map(d => d.cost || 0),
        lineStyle: { color: '#5bc0de', width: 2 },
        itemStyle: { color: '#5bc0de' },
        areaStyle: { 
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(91, 192, 222, 0.4)' },
              { offset: 1, color: 'rgba(91, 192, 222, 0.05)' }
            ]
          }
        },
        symbol: 'circle',
        symbolSize: 4,
        label: {
          show: data.length <= 20,
          position: 'top',
          formatter: (p) => '$' + formatNumber(p.value),
          fontSize: 8,
          color: '#666',
        }
      }],
    };
  }, [data]);

  if (!data || data.length === 0) {
    return <div className="text-center text-muted py-4">Sin datos</div>;
  }

  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 220 }} />;
};

// Pie Chart de Distribución por Edad
const AgePieChart = ({ data }) => {
  const ageColors = ['#ff6b6b', '#feca57', '#48dbfb', '#1dd1a1', '#5f27cd', '#ff9ff3', '#54a0ff'];
  
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
        textStyle: { fontSize: 11 }
      },
      series: [{
        type: 'pie',
        radius: ['45%', '75%'],
        center: ['35%', '50%'],
        data: data.map((d, i) => ({
          name: d.ageRange,
          value: d.impressions,
          itemStyle: { color: ageColors[i % ageColors.length] }
        })),
        label: { show: false },
        emphasis: {
          label: { show: true, fontSize: 12, fontWeight: 'bold' }
        }
      }]
    };
  }, [data]);

  if (!data || data.length === 0) {
    return <div className="text-center text-muted py-4">Sin datos</div>;
  }

  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 200 }} />;
};

// Viewability Rates por Plataforma
const ViewabilityChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="text-center text-muted py-4">Sin datos de viewability</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-center mb-2 gap-3" style={{ fontSize: '10px' }}>
        <span><span className="rounded-circle d-inline-block me-1" style={{ width: 10, height: 10, backgroundColor: '#999' }}></span>Started</span>
        <span><span className="rounded-circle d-inline-block me-1" style={{ width: 10, height: 10, backgroundColor: '#777' }}></span>video_25</span>
        <span><span className="rounded-circle d-inline-block me-1" style={{ width: 10, height: 10, backgroundColor: '#555' }}></span>video_50</span>
        <span><span className="rounded-circle d-inline-block me-1" style={{ width: 10, height: 10, backgroundColor: '#444' }}></span>video_75</span>
        <span><span className="rounded-circle d-inline-block me-1" style={{ width: 10, height: 10, backgroundColor: '#2d2d2d' }}></span>Fully Played</span>
      </div>
      {data.map((platform, idx) => {
        const total = platform.videoViews || 1;
        const v25 = ((platform.video25 || 0) / total * 100);
        const v50 = ((platform.video50 || 0) / total * 100);
        const v75 = ((platform.video75 || 0) / total * 100);
        const v100 = ((platform.video100 || 0) / total * 100);
        const started = 100 - v25 - v50 - v75 - v100;
        
        return (
          <div key={idx} className="mb-2">
            <div className="d-flex align-items-center">
              <div className="fw-bold me-2" style={{ width: 60, fontSize: '11px' }}>{platform.platform}</div>
              <div className="flex-grow-1 d-flex" style={{ height: 22 }}>
                <div style={{ width: `${Math.max(started, 0)}%`, backgroundColor: '#999' }} className="d-flex align-items-center justify-content-center">
                  <small className="text-white" style={{ fontSize: '9px' }}>{started > 10 ? started.toFixed(0) + '%' : ''}</small>
                </div>
                <div style={{ width: `${v25}%`, backgroundColor: '#777' }} className="d-flex align-items-center justify-content-center">
                  <small className="text-white" style={{ fontSize: '9px' }}>{v25 > 10 ? v25.toFixed(0) + '%' : ''}</small>
                </div>
                <div style={{ width: `${v50}%`, backgroundColor: '#555' }} className="d-flex align-items-center justify-content-center">
                  <small className="text-white" style={{ fontSize: '9px' }}>{v50 > 10 ? v50.toFixed(0) + '%' : ''}</small>
                </div>
                <div style={{ width: `${v75}%`, backgroundColor: '#444' }} className="d-flex align-items-center justify-content-center">
                  <small className="text-white" style={{ fontSize: '9px' }}>{v75 > 10 ? v75.toFixed(0) + '%' : ''}</small>
                </div>
                <div style={{ width: `${v100}%`, backgroundColor: '#2d2d2d' }} className="d-flex align-items-center justify-content-center">
                  <small className="text-white" style={{ fontSize: '9px' }}>{v100 > 5 ? v100.toFixed(0) + '%' : ''}</small>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Métricas Calculadas Card
const CalculatedMetricsCard = ({ kpis }) => {
  const totalImpressions = kpis.impressions || 0;
  const totalVideoViews = kpis.videoViews || 0;
  const totalClicks = kpis.clicks || 0;
  const totalReach = kpis.reach || 0;
  
  // Calcular métricas
  const brandAwareness = totalReach > 0 ? (totalReach / 1000000).toFixed(2) : 0; // En millones
  const productAwareness = totalImpressions > 0 ? ((totalImpressions / 1000000) * 13).toFixed(0) : 0; // % estimado
  const frequency = totalReach > 0 ? (totalImpressions / totalReach).toFixed(2) : 0;
  const videoViewRate = totalImpressions > 0 ? ((totalVideoViews / totalImpressions) * 100).toFixed(0) : 0;
  const engagement = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(0) : 0;

  const metrics = [
    { label: 'BRAND AWARENESS', value: brandAwareness + 'M', color: '#9b59b6', opacity: 0.3 },
    { label: 'PRODUCT AWARENESS', value: productAwareness + '%', color: '#9b59b6', opacity: 0.6, sublabel: '(impresiones)' },
    { label: 'FREQUENCY', value: frequency, color: '#9b59b6', opacity: 0.7 },
    { label: 'VIDEO VIEWS', value: videoViewRate + '%', color: '#9b59b6', opacity: 0.8 },
    { label: 'ENGAGEMENT', value: engagement + '%', color: '#9b59b6', opacity: 1 },
  ];

  return (
    <Card className="shadow-sm border-0">
      <Card.Header className="bg-white border-bottom py-2">
        <h6 className="mb-0" style={{ fontSize: '13px' }}>
          🎯 NOTORIEDAD - OBJETIVOS MEDIA
        </h6>
        <small className="text-muted">Maximizar product awareness y primacía</small>
      </Card.Header>
      <Card.Body>
        <div className="d-flex gap-2">
          {metrics.map((m, idx) => (
            <div 
              key={idx} 
              className="flex-fill text-center p-2 rounded"
              style={{ 
                backgroundColor: `rgba(155, 89, 182, ${m.opacity})`,
                color: m.opacity > 0.5 ? 'white' : '#333',
                border: idx === 0 ? '2px dashed #9b59b6' : 'none'
              }}
            >
              <div className="fw-bold" style={{ fontSize: '10px' }}>{m.label}</div>
              {m.sublabel && <div style={{ fontSize: '8px' }}>{m.sublabel}</div>}
              <div className="fw-bold" style={{ fontSize: '14px' }}>{m.value}</div>
            </div>
          ))}
        </div>
        <div className="text-center mt-2">
          <small className="text-muted">MÉTRICAS CALCULADAS</small>
        </div>
      </Card.Body>
    </Card>
  );
};

// Gráfico de distribución por plataforma
const PlatformPieChart = ({ data }) => {
  const options = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        data: data.map(d => ({
          name: d.platform,
          value: d.cost,
          itemStyle: { color: platformColors[d.platform] || '#95aac9' }
        })),
        label: {
          formatter: '{b}\n{d}%',
          fontSize: 11,
        }
      }]
    };
  }, [data]);

  if (!data || data.length === 0) {
    return <div className="text-center text-muted py-4">Sin datos</div>;
  }

  return <ReactEChartsCore echarts={echarts} option={options} style={{ height: 250 }} />;
};

// Tabla de Campañas (con filtros)
const CampaignsTable = ({ data, sortBy, onSort, filters = {}, onFilterChange }) => {
  const f = filters || {};
  const filtered = useMemo(() => {
    if (!data?.length) return [];
    return data.filter((row) => {
      if (f.campaignName && !(row.campaignName || '').toLowerCase().includes((f.campaignName || '').toLowerCase())) return false;
      if (f.productType && (row.productType || '').toLowerCase() !== (f.productType || '').toLowerCase()) return false;
      if (f.platform && (row.platform || '').toLowerCase() !== (f.platform || '').toLowerCase()) return false;
      return true;
    });
  }, [data, f.campaignName, f.productType, f.platform]);
  const sortedData = useMemo(() => [...filtered].sort((a, b) => {
    const aVal = a[sortBy.field];
    const bVal = b[sortBy.field];
    return sortBy.direction === 'desc' ? bVal - aVal : aVal - bVal;
  }), [filtered, sortBy]);

  if (sortedData.length === 0 && (!data || data.length === 0)) {
    return <div className="text-center text-muted py-4">Sin campañas</div>;
  }

  return (
    <div className="table-responsive">
      <Table hover size="sm" className="mb-0" style={{ fontSize: '11px' }}>
        <thead className="bg-light">
          <tr>
            <th>Campaña</th>
            <th className="text-center">Producto</th>
            <th className="text-center">Plataforma</th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('impressions')}>
              Impresiones {sortBy.field === 'impressions' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('clicks')}>
              Clics (Todos) {sortBy.field === 'clicks' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('linkClicks')}>
              Link Clics {sortBy.field === 'linkClicks' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('cpc')}>
              CPC {sortBy.field === 'cpc' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('linkCpc')}>
              Link CPC {sortBy.field === 'linkCpc' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('leads')}>
              Leads {sortBy.field === 'leads' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('whatsappConversations')}>
              WA Conv. {sortBy.field === 'whatsappConversations' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('cost')}>
              Inversión {sortBy.field === 'cost' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
          </tr>
          <tr className="bg-white border-bottom">
            <th style={{ padding: '4px 6px' }}>
              <Form.Control size="sm" placeholder="Filtrar campaña..." value={f.campaignName || ''} onChange={(e) => onFilterChange?.({ ...filters, campaignName: e.target.value })} style={{ fontSize: '10px' }} />
            </th>
            <th style={{ padding: '4px 6px' }}>
              <Form.Control size="sm" placeholder="Producto" value={f.productType || ''} onChange={(e) => onFilterChange?.({ ...filters, productType: e.target.value })} style={{ fontSize: '10px' }} />
            </th>
            <th style={{ padding: '4px 6px' }}>
              <Form.Select size="sm" value={f.platform || ''} onChange={(e) => onFilterChange?.({ ...filters, platform: e.target.value })} style={{ fontSize: '10px' }}>
                <option value="">Todas</option>
                <option value="META">META</option>
                <option value="GOOGLE">GOOGLE</option>
                <option value="TIKTOK">TIKTOK</option>
                <option value="LINKEDIN">LINKEDIN</option>
              </Form.Select>
            </th>
            <th colSpan={8} className="text-muted small" style={{ padding: '4px 8px', verticalAlign: 'middle' }}>Filtros · {sortedData.length} resultado(s)</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.slice(0, 50).map((row, idx) => (
            <tr key={idx}>
              <td>
                <div className="fw-semibold text-truncate" style={{ maxWidth: '200px' }}>
                  {row.campaignName}
                </div>
                <small className="text-muted">{row.accountName?.slice(0, 30)}</small>
              </td>
              <td className="text-center">
                <Badge 
                  style={{ backgroundColor: productColors[row.productType], fontSize: '9px' }}
                >
                  {row.productType}
                </Badge>
              </td>
              <td className="text-center">
                <Badge 
                  style={{ backgroundColor: platformColors[row.platform], fontSize: '9px' }}
                >
                  {row.platform}
                </Badge>
              </td>
              <td className="text-end">{formatNumber(row.impressions)}</td>
              <td className="text-end">{formatNumber(row.clicks)}</td>
              <td className="text-end">{formatNumber(row.linkClicks)}</td>
              <td className="text-end">{formatCurrency(row.cpc)}</td>
              <td className="text-end">{formatCurrency(row.linkCpc)}</td>
              <td className="text-end">{formatNumber(row.leads)}</td>
              <td className="text-end" style={{ color: row.whatsappConversations > 0 ? '#25d366' : undefined }}>
                {formatNumber(row.whatsappConversations)}
              </td>
              <td className="text-end fw-bold">{formatCurrency(row.cost)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// Tabla de AdGroups (con filtros)
const AdGroupsTable = ({ data, sortBy, onSort, filters = {}, onFilterChange }) => {
  const f = filters || {};
  const filtered = useMemo(() => {
    if (!data?.length) return [];
    return data.filter((row) => {
      if (f.adGroupName && !(row.adGroupName || '').toLowerCase().includes((f.adGroupName || '').toLowerCase())) return false;
      if (f.campaignName && !(row.campaignName || '').toLowerCase().includes((f.campaignName || '').toLowerCase())) return false;
      return true;
    });
  }, [data, f.adGroupName, f.campaignName]);
  const sortedData = useMemo(() => [...filtered].sort((a, b) => {
    const aVal = a[sortBy.field];
    const bVal = b[sortBy.field];
    return sortBy.direction === 'desc' ? bVal - aVal : aVal - bVal;
  }), [filtered, sortBy]);

  if (sortedData.length === 0 && (!data || data.length === 0)) {
    return <div className="text-center text-muted py-4">Sin grupos de anuncios</div>;
  }

  return (
    <div className="table-responsive">
      <Table hover size="sm" className="mb-0" style={{ fontSize: '11px' }}>
        <thead className="bg-light">
          <tr>
            <th>Grupo de Anuncios</th>
            <th>Campaña</th>
            <th className="text-center">Producto</th>
            <th className="text-center">Plataforma</th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('impressions')}>
              Impresiones {sortBy.field === 'impressions' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('clicks')}>
              Clicks {sortBy.field === 'clicks' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end">CTR</th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('leads')}>
              Leads {sortBy.field === 'leads' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('cost')}>
              Inversión {sortBy.field === 'cost' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
          </tr>
          <tr className="bg-white border-bottom">
            <th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar grupo..." value={f.adGroupName || ''} onChange={(e) => onFilterChange?.({ ...filters, adGroupName: e.target.value })} style={{ fontSize: '10px' }} /></th>
            <th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar campaña..." value={f.campaignName || ''} onChange={(e) => onFilterChange?.({ ...filters, campaignName: e.target.value })} style={{ fontSize: '10px' }} /></th>
            <th colSpan={6} className="text-muted small" style={{ padding: '4px 8px', verticalAlign: 'middle' }}>Filtros · {sortedData.length} resultado(s)</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.slice(0, 50).map((row, idx) => (
            <tr key={idx}>
              <td>
                <div className="fw-semibold text-truncate" style={{ maxWidth: '200px' }}>{row.adGroupName}</div>
                <small className="text-muted">{row.accountName?.slice(0, 30)}</small>
              </td>
              <td>
                <div className="text-truncate" style={{ maxWidth: '150px', fontSize: '10px' }}>{row.campaignName}</div>
              </td>
              <td className="text-center">
                <Badge style={{ backgroundColor: productColors[row.productType], fontSize: '9px' }}>
                  {row.productType}
                </Badge>
              </td>
              <td className="text-center">
                <Badge style={{ backgroundColor: platformColors[row.platform], fontSize: '9px' }}>
                  {row.platform}
                </Badge>
              </td>
              <td className="text-end">{formatNumber(row.impressions)}</td>
              <td className="text-end">{formatNumber(row.clicks)}</td>
              <td className="text-end">
                <Badge bg={row.ctr > 1 ? 'success' : row.ctr > 0.5 ? 'warning' : 'secondary'}>
                  {formatPercent(row.ctr)}
                </Badge>
              </td>
              <td className="text-end">{formatNumber(row.leads)}</td>
              <td className="text-end fw-bold">{formatCurrency(row.cost)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// Tabla de Ads (con filtros)
const AdsTable = ({ data, sortBy, onSort, filters = {}, onFilterChange }) => {
  const f = filters || {};
  const filtered = useMemo(() => {
    if (!data?.length) return [];
    return data.filter((row) => {
      if (f.adName && !(row.adName || '').toLowerCase().includes((f.adName || '').toLowerCase())) return false;
      if (f.campaignName && !(row.campaignName || '').toLowerCase().includes((f.campaignName || '').toLowerCase())) return false;
      if (f.adGroupName && !(row.adGroupName || '').toLowerCase().includes((f.adGroupName || '').toLowerCase())) return false;
      return true;
    });
  }, [data, f.adName, f.campaignName, f.adGroupName]);
  const sortedData = useMemo(() => [...filtered].sort((a, b) => {
    const aVal = a[sortBy.field];
    const bVal = b[sortBy.field];
    return sortBy.direction === 'desc' ? bVal - aVal : aVal - bVal;
  }), [filtered, sortBy]);

  if (sortedData.length === 0 && (!data || data.length === 0)) {
    return <div className="text-center text-muted py-4">Sin anuncios</div>;
  }

  return (
    <div className="table-responsive">
      <Table hover size="sm" className="mb-0" style={{ fontSize: '11px' }}>
        <thead className="bg-light">
          <tr>
            <th>Anuncio</th>
            <th>Grupo</th>
            <th className="text-center">Producto</th>
            <th className="text-center">Plataforma</th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('impressions')}>
              Impresiones {sortBy.field === 'impressions' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('clicks')}>
              Clics {sortBy.field === 'clicks' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('linkClicks')}>
              Link Clics {sortBy.field === 'linkClicks' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end">CTR</th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('leads')}>
              Leads {sortBy.field === 'leads' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('whatsappConversations')}>
              WA Conv. {sortBy.field === 'whatsappConversations' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => onSort('cost')}>
              Inversión {sortBy.field === 'cost' && (sortBy.direction === 'desc' ? '↓' : '↑')}
            </th>
          </tr>
          <tr className="bg-white border-bottom">
            <th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar anuncio..." value={f.adName || ''} onChange={(e) => onFilterChange?.({ ...filters, adName: e.target.value })} style={{ fontSize: '10px' }} /></th>
            <th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar grupo..." value={f.adGroupName || ''} onChange={(e) => onFilterChange?.({ ...filters, adGroupName: e.target.value })} style={{ fontSize: '10px' }} /></th>
            <th style={{ padding: '4px 6px' }}><Form.Control size="sm" placeholder="Filtrar campaña..." value={f.campaignName || ''} onChange={(e) => onFilterChange?.({ ...filters, campaignName: e.target.value })} style={{ fontSize: '10px' }} /></th>
            <th colSpan={8} className="text-muted small" style={{ padding: '4px 8px', verticalAlign: 'middle' }}>Filtros · {sortedData.length} resultado(s)</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.slice(0, 50).map((row, idx) => (
            <tr key={idx}>
              <td>
                <div className="fw-semibold text-truncate" style={{ maxWidth: '200px' }}>
                  {row.adName}
                </div>
                <small className="text-muted">{row.campaignName?.slice(0, 30)}</small>
              </td>
              <td>
                <div className="text-truncate" style={{ maxWidth: '120px', fontSize: '10px' }}>
                  {row.adGroupName}
                </div>
              </td>
              <td className="text-center">
                <Badge style={{ backgroundColor: productColors[row.productType], fontSize: '9px' }}>
                  {row.productType}
                </Badge>
              </td>
              <td className="text-center">
                <Badge style={{ backgroundColor: platformColors[row.platform], fontSize: '9px' }}>
                  {row.platform}
                </Badge>
              </td>
              <td className="text-end">{formatNumber(row.impressions)}</td>
              <td className="text-end">{formatNumber(row.clicks)}</td>
              <td className="text-end">{formatNumber(row.linkClicks)}</td>
              <td className="text-end">
                <Badge bg={row.ctr > 1 ? 'success' : row.ctr > 0.5 ? 'warning' : 'secondary'}>
                  {formatPercent(row.ctr)}
                </Badge>
              </td>
              <td className="text-end">{formatNumber(row.leads)}</td>
              <td className="text-end" style={{ color: row.whatsappConversations > 0 ? '#25d366' : undefined }}>
                {formatNumber(row.whatsappConversations)}
              </td>
              <td className="text-end fw-bold">{formatCurrency(row.cost)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// Main Dashboard Component
const ClaroDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [dateTo, setDateTo] = useState(new Date());
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [sortBy, setSortBy] = useState({ field: 'cost', direction: 'desc' });
  const [filterCampaigns, setFilterCampaigns] = useState({ campaignName: '', productType: '', platform: '' });
  const [filterAdGroups, setFilterAdGroups] = useState({ adGroupName: '', campaignName: '' });
  const [filterAds, setFilterAds] = useState({ adName: '', campaignName: '', adGroupName: '' });
  const [activeTab, setActiveTab] = useState('overall');
  const [overallSection, setOverallSection] = useState(0); // Para navegar dentro de Overall

  // Cargar datos
  const loadData = useCallback(async (isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const result = await budgetService.getClaroDashboardMetrics({
        dateFrom: dateFrom.toISOString().split('T')[0],
        dateTo: dateTo.toISOString().split('T')[0],
        productType: selectedProduct || undefined,
        platform: selectedPlatform || undefined,
        accountId: selectedAccount || undefined,
        campaignId: selectedCampaign || undefined,
      });

      if (result.success) {
        setData(result.data);
      } else {
        toast.error(result.error || 'Error cargando datos');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error cargando el dashboard');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [dateFrom, dateTo, selectedProduct, selectedPlatform, selectedAccount, selectedCampaign]);

  // Carga inicial
  useEffect(() => {
    loadData(true);
  }, []);

  // Recargar cuando cambian filtros
  useEffect(() => {
    if (!loading && data) {
      loadData(false);
    }
  }, [dateFrom, dateTo, selectedProduct, selectedPlatform, selectedAccount, selectedCampaign]); // eslint-disable-line

  // Handle date presets
  const handleDatePreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateFrom(from);
    setDateTo(to);
  };

  // Handle sort
  const handleSort = (field) => {
    setSortBy(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Handle product click - reset cascading filters
  const handleProductClick = (product) => {
    setSelectedProduct(prev => {
      const next = prev === product ? '' : product;
      setSelectedAccount('');
      setSelectedCampaign('');
      return next;
    });
  };

  const kpis = data?.kpis || {};

  if (loading) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Cargando dashboard Claro...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      {/* Indicador de carga cuando se aplican filtros */}
      {isRefreshing && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(255,255,255,0.85)', zIndex: 9999 }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-muted fw-medium">Aplicando filtros...</p>
          </div>
        </div>
      )}

      {/* Header con Productos */}
      <Card className="mb-3 shadow-sm border-0" style={{ backgroundColor: '#1a1a1a' }}>
        <Card.Body className="py-3">
          <Row className="align-items-center">
            <Col xs={12} md={2}>
              <div className="d-flex align-items-center">
                <div 
                  className="rounded-circle me-2 d-flex align-items-center justify-content-center"
                  style={{ width: 40, height: 40, backgroundColor: '#e63757' }}
                >
                  <span className="text-white" style={{ fontSize: '18px' }}>📱</span>
                </div>
                <div>
                  <h5 className="mb-0 text-white fw-bold">CLARO</h5>
                  <small className="text-white-50">Dashboard</small>
                </div>
              </div>
            </Col>
            <Col xs={12} md={10}>
              <div className="d-flex gap-2 flex-wrap justify-content-end">
                {['EQUIPOS', 'HOGAR', 'PREPAGO', 'POSTPAGO', 'BRAND', 'CORPORATIVO'].map(product => (
                  <ProductTab
                    key={product}
                    product={product}
                    isActive={selectedProduct === product}
                    onClick={() => handleProductClick(product)}
                    data={data?.byProduct}
                  />
                ))}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Filtros */}
      <Card className="mb-3 shadow-sm border-0">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col xs={6} md={2}>
              <Form.Label className="small text-muted mb-1">Fecha inicio</Form.Label>
              <DatePicker
                selected={dateFrom}
                onChange={setDateFrom}
                dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm"
                maxDate={dateTo}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small text-muted mb-1">Fecha fin</Form.Label>
              <DatePicker
                selected={dateTo}
                onChange={setDateTo}
                dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm"
                minDate={dateFrom}
                maxDate={new Date()}
              />
            </Col>
            <Col xs={12} md="auto">
              <div className="btn-group btn-group-sm">
                <Button variant="outline-secondary" onClick={() => handleDatePreset(7)}>7d</Button>
                <Button variant="outline-secondary" onClick={() => handleDatePreset(30)}>30d</Button>
                <Button variant="outline-secondary" onClick={() => handleDatePreset(90)}>90d</Button>
              </div>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small text-muted mb-1">Plataforma</Form.Label>
              <Select
                value={selectedPlatform ? { value: selectedPlatform, label: selectedPlatform } : null}
                onChange={(opt) => setSelectedPlatform(opt ? opt.value : '')}
                options={[
                  { value: 'META', label: 'Meta Ads' },
                  { value: 'GOOGLE', label: 'Google Ads' },
                  { value: 'TIKTOK', label: 'TikTok Ads' },
                  { value: 'LINKEDIN', label: 'LinkedIn Ads' },
                ]}
                placeholder="Todas"
                isClearable
                isSearchable
                classNamePrefix="react-select"
                styles={{ control: (base) => ({ ...base, minHeight: 31, fontSize: 13 }) }}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small text-muted mb-1">Cuenta</Form.Label>
              <Select
                value={selectedAccount ? { 
                  value: selectedAccount, 
                  label: data?.filters?.accounts?.find(a => a.accountKey === parseInt(selectedAccount))?.accountName 
                } : null}
                onChange={(opt) => { setSelectedAccount(opt ? String(opt.value) : ''); setSelectedCampaign(''); }}
                options={(data?.filters?.accounts || [])
                  .filter(a => {
                    if (selectedProduct && a.productType && a.productType !== selectedProduct) return false;
                    if (selectedPlatform && a.sourcePlatform && a.sourcePlatform !== selectedPlatform) return false;
                    return true;
                  })
                  .map(a => ({ value: a.accountKey, label: a.accountName }))}
                placeholder="Todas"
                isClearable
                isSearchable
                classNamePrefix="react-select"
                styles={{ control: (base) => ({ ...base, minHeight: 31, fontSize: 13 }) }}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small text-muted mb-1">Campaña</Form.Label>
              <Select
                value={selectedCampaign ? { 
                  value: selectedCampaign, 
                  label: data?.filters?.campaigns?.find(c => c.campaignKey === parseInt(selectedCampaign))?.campaignName?.slice(0, 30)
                } : null}
                onChange={(opt) => setSelectedCampaign(opt ? String(opt.value) : '')}
                options={(data?.filters?.campaigns || [])
                  .filter(c => {
                    if (selectedAccount && c.accountKey !== parseInt(selectedAccount)) return false;
                    if (selectedProduct && c.productType && c.productType !== selectedProduct) return false;
                    if (selectedPlatform && c.sourcePlatform && c.sourcePlatform !== selectedPlatform) return false;
                    return true;
                  })
                  .map(c => ({ value: c.campaignKey, label: c.campaignName }))}
                placeholder="Todas"
                isClearable
                isSearchable
                classNamePrefix="react-select"
                styles={{ control: (base) => ({ ...base, minHeight: 31, fontSize: 13 }) }}
              />
            </Col>
          </Row>
          
          {/* Filtros activos */}
          {(selectedProduct || selectedPlatform || selectedAccount || selectedCampaign) && (
            <div className="mt-2 pt-2 border-top">
              <small className="text-muted me-2">Filtros activos:</small>
              {selectedProduct && (
                <Badge bg="danger" className="me-1" style={{ cursor: 'pointer' }} onClick={() => setSelectedProduct('')}>
                  Producto: {selectedProduct} ×
                </Badge>
              )}
              {selectedPlatform && (
                <Badge bg="primary" className="me-1" style={{ cursor: 'pointer' }} onClick={() => setSelectedPlatform('')}>
                  Plataforma: {selectedPlatform} ×
                </Badge>
              )}
              {selectedAccount && (
                <Badge bg="warning" text="dark" className="me-1" style={{ cursor: 'pointer' }} onClick={() => { setSelectedAccount(''); setSelectedCampaign(''); }}>
                  Cuenta ×
                </Badge>
              )}
              {selectedCampaign && (
                <Badge bg="secondary" className="me-1" style={{ cursor: 'pointer' }} onClick={() => setSelectedCampaign('')}>
                  Campaña ×
                </Badge>
              )}
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 ms-2 text-danger"
                onClick={() => { setSelectedProduct(''); setSelectedPlatform(''); setSelectedAccount(''); setSelectedCampaign(''); }}
              >
                Limpiar todos
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* KPIs Row 1 - Volumen y Performance Click */}
      <Row className="g-2 mb-2">
        <Col xs={6} md>
          <KPICard title="Inversión" value={formatCurrency(kpis.cost)} color="#e63757" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Impresiones" value={formatNumber(kpis.impressions)} color="#2c7be5" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Clics (Todos)" value={formatNumber(kpis.clicks)} subtitle={`CPC: ${formatCurrency(kpis.cpc)}`} color="#00d97e" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Clics en Enlace" value={formatNumber(kpis.linkClicks)} subtitle={`CPC: ${formatCurrency(kpis.linkCpc)}`} color="#27bcfd" />
        </Col>
        <Col xs={6} md>
          <KPICard title="CTR" value={formatPercent(kpis.ctr)} subtitle={`Link CTR: ${formatPercent(kpis.linkCtr)}`} color="#f5803e" />
        </Col>
      </Row>

      {/* KPIs Row 2 - Conversiones */}
      <Row className="g-2 mb-2">
        <Col xs={6} md>
          <KPICard title="Leads (Total)" value={formatNumber(kpis.leads)} subtitle={`CPL: ${formatCurrency(kpis.cpl)}`} color="#6b5eae" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Registros Web" value={formatNumber(kpis.leadsWebsiteRegistration)} color="#9d6bae" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Leads en Facebook" value={formatNumber(kpis.leadsOnFacebook)} color="#3b5998" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Conv. WhatsApp" value={formatNumber(kpis.whatsappConversations)} subtitle={kpis.costPerWhatsappConversation > 0 ? `CP-Conv: ${formatCurrency(kpis.costPerWhatsappConversation)}` : ''} color="#25d366" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Alcance" value={formatNumber(kpis.reach)} subtitle={`Frec: ${(kpis.frequency || 0).toFixed(2)}`} color="#1a73e8" />
        </Col>
      </Row>

      {/* KPIs Row 2 - Video */}
      <Row className="g-2 mb-3">
        <Col xs={6} md>
          <KPICard title="Video Views" value={formatNumber(kpis.videoViews)} color="#e63757" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Vistas 25%" value={formatNumber(kpis.video25)} color="#f5803e" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Vistas 50%" value={formatNumber(kpis.video50)} color="#f5803e" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Vistas 75%" value={formatNumber(kpis.video75)} color="#00d97e" />
        </Col>
        <Col xs={6} md>
          <KPICard title="Vistas 100%" value={formatNumber(kpis.video100)} color="#00d97e" />
        </Col>
      </Row>

      {/* Tabs de navegación */}
      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Card className="shadow-sm border-0 mb-3">
          <Card.Body className="py-0">
            <Nav variant="pills" className="nav-pills-falcon py-2">
              <Nav.Item>
                <Nav.Link eventKey="overall" className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'overall' ? 'bg-danger text-white' : 'bg-light text-dark'}`}>
                  Overall
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="campaigns" className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'campaigns' ? 'bg-danger text-white' : 'bg-light text-dark'}`}>
                  Campañas
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="adgroups" className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'adgroups' ? 'bg-danger text-white' : 'bg-light text-dark'}`}>
                  AdGroups
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="ads" className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'ads' ? 'bg-danger text-white' : 'bg-light text-dark'}`}>
                  Anuncios
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="platforms" className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'platforms' ? 'bg-danger text-white' : 'bg-light text-dark'}`}>
                  Plataformas
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="geographic" className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'geographic' ? 'bg-danger text-white' : 'bg-light text-dark'}`}>
                  Geográfico
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="demographics" className={`px-4 py-2 me-2 rounded-pill ${activeTab === 'demographics' ? 'bg-danger text-white' : 'bg-light text-dark'}`}>
                  Demografía
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Body>
        </Card>

        <Tab.Content>
          {/* Tab Overall con navegación por secciones */}
          <Tab.Pane eventKey="overall">
            {/* Navegador de secciones */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Button 
                variant="outline-secondary" 
                onClick={() => setOverallSection(prev => Math.max(0, prev - 1))}
                disabled={overallSection === 0}
                className="rounded-circle"
                style={{ width: 40, height: 40 }}
              >
                ←
              </Button>
              
              <div className="d-flex gap-2">
                {['Evolutivo', 'Métricas Detalladas', 'Tendencias', 'Canales & Video', 'Productos'].map((label, idx) => (
                  <Badge 
                    key={idx}
                    bg={overallSection === idx ? 'danger' : 'light'}
                    text={overallSection === idx ? 'white' : 'dark'}
                    className="px-3 py-2"
                    style={{ cursor: 'pointer', fontSize: '11px' }}
                    onClick={() => setOverallSection(idx)}
                  >
                    {label}
                  </Badge>
                ))}
              </div>
              
              <Button 
                variant="outline-secondary" 
                onClick={() => setOverallSection(prev => Math.min(4, prev + 1))}
                disabled={overallSection === 4}
                className="rounded-circle"
                style={{ width: 40, height: 40 }}
              >
                →
              </Button>
            </div>

            {/* Sección 0: Gráfico Evolutivo Principal */}
            {overallSection === 0 && (
              <EvolutiveChart data={data?.dailyTrend} />
            )}

            {/* Sección 1: 4 gráficos de métricas detalladas */}
            {overallSection === 1 && (
              <Row className="g-2">
                <Col lg={6} md={6}>
                  <DualAxisChart 
                    data={data?.dailyTrend}
                    title="Impresiones vs CPM"
                    barKey="impressions"
                    lineKey="cpm"
                    barLabel="Impresiones"
                    lineLabel="CPM"
                    barColor="#5b9bd5"
                    lineColor="#f5803e"
                    icon="chart-bar"
                  />
                </Col>
                <Col lg={6} md={6}>
                  <DualAxisChart 
                    data={data?.dailyTrend}
                    title="Clicks vs CTR"
                    barKey="clicks"
                    lineKey="ctr"
                    barLabel="Clicks"
                    lineLabel="CTR"
                    barColor="#70ad47"
                    lineColor="#f5803e"
                    icon="mouse-pointer"
                  />
                </Col>
                <Col lg={6} md={6}>
                  <DualAxisChart 
                    data={data?.dailyTrend}
                    title="Leads vs CPL"
                    barKey="leads"
                    lineKey="cpl"
                    barLabel="Leads"
                    lineLabel="CPL"
                    barColor="#9b59b6"
                    lineColor="#f5803e"
                    icon="user-plus"
                  />
                </Col>
                <Col lg={6} md={6}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-white border-bottom py-2">
                      <h6 className="mb-0" style={{ fontSize: '13px' }}>
                        💵 Costo por Día
                      </h6>
                    </Card.Header>
                    <Card.Body className="p-2">
                      <CostPerDayChart data={data?.dailyCost} />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Sección 2: Tendencia General + Distribución Edad */}
            {overallSection === 2 && (
              <Row className="g-2">
                <Col lg={8}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-white border-bottom py-2">
                      <h6 className="mb-0" style={{ fontSize: '13px' }}>
                        📊 Tendencia General - Impresiones/Clicks/Gasto
                      </h6>
                    </Card.Header>
                    <Card.Body className="p-2">
                      <TrendChart data={data?.dailyTrend} />
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={4}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header className="bg-white border-bottom py-2">
                      <h6 className="mb-0" style={{ fontSize: '13px' }}>
                        👥 Distribución por Edad
                      </h6>
                    </Card.Header>
                    <Card.Body className="p-2">
                      <AgePieChart data={data?.byAge} />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Sección 3: Inversión por Canales + Viewability + Métricas Calculadas */}
            {overallSection === 3 && (
              <>
                <Row className="g-2 mb-3">
                  <Col lg={6}>
                    <Card className="shadow-sm border-0 h-100">
                      <Card.Header className="bg-white border-bottom py-2">
                        <h6 className="mb-0" style={{ fontSize: '13px' }}>
                          🥧 Inversión por Canales
                        </h6>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <PlatformPieChart data={data?.byPlatform} />
                          </Col>
                          <Col md={6}>
                            <div className="table-responsive">
                              <Table size="sm" className="mb-0" style={{ fontSize: '11px' }}>
                                <thead className="bg-light">
                                  <tr>
                                    <th>Plataforma</th>
                                    <th className="text-end">Inversión</th>
                                    <th className="text-end">%</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {data?.byPlatform?.map((p, idx) => (
                                    <tr key={idx}>
                                      <td>
                                        <Badge style={{ backgroundColor: platformColors[p.platform] }}>{p.platform}</Badge>
                                      </td>
                                      <td className="text-end fw-bold">{formatCurrency(p.cost)}</td>
                                      <td className="text-end">{p.percentage}%</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={6}>
                    <Card className="shadow-sm border-0 h-100">
                      <Card.Header className="bg-white border-bottom py-2">
                        <h6 className="mb-0" style={{ fontSize: '13px' }}>
                          📹 VIEWABILITY RATES X MEDIO
                        </h6>
                      </Card.Header>
                      <Card.Body>
                        <ViewabilityChart data={data?.byPlatform} />
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                <Row className="g-2">
                  <Col lg={12}>
                    <CalculatedMetricsCard kpis={kpis} />
                  </Col>
                </Row>
              </>
            )}

            {/* Sección 4: Tabla por producto */}
            {overallSection === 4 && (
              <Card className="shadow-sm border-0">
              <Card.Header className="bg-white border-bottom py-2">
                <h6 className="mb-0">
                  📦 Métricas por Tipo de Producto
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0" style={{ fontSize: '11px' }}>
                    <thead className="bg-light">
                      <tr>
                        <th>Producto</th>
                        <th className="text-end">Impresiones</th>
                        <th className="text-end">Clicks</th>
                        <th className="text-end">CTR</th>
                        <th className="text-end">Video Views</th>
                        <th className="text-end">V.25%</th>
                        <th className="text-end">V.50%</th>
                        <th className="text-end">V.75%</th>
                        <th className="text-end">V.100%</th>
                        <th className="text-end">Leads</th>
                        <th className="text-end">CPL</th>
                        <th className="text-end">Inversión</th>
                        <th className="text-end">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.byProduct?.filter(p => p.productType !== 'OTRO').map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            <Badge style={{ backgroundColor: productColors[row.productType] }}>
                              {row.productType}
                            </Badge>
                          </td>
                          <td className="text-end">{formatNumber(row.impressions)}</td>
                          <td className="text-end">{formatNumber(row.clicks)}</td>
                          <td className="text-end">{formatPercent(row.ctr)}</td>
                          <td className="text-end">{formatNumber(row.videoViews)}</td>
                          <td className="text-end">{formatNumber(row.video25)}</td>
                          <td className="text-end">{formatNumber(row.video50)}</td>
                          <td className="text-end">{formatNumber(row.video75)}</td>
                          <td className="text-end">{formatNumber(row.video100)}</td>
                          <td className="text-end">{formatNumber(row.leads)}</td>
                          <td className="text-end">{formatCurrency(row.cpl)}</td>
                          <td className="text-end fw-bold" style={{ color: productColors[row.productType] }}>
                            {formatCurrency(row.cost)}
                          </td>
                          <td className="text-end">{row.percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
            )}
          </Tab.Pane>

          {/* Tab Campañas */}
          <Tab.Pane eventKey="campaigns">
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white border-bottom py-2">
                <h6 className="mb-0">
                  📋 Detalle de Campañas ({data?.campaigns?.length || 0})
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <CampaignsTable data={data?.campaigns} sortBy={sortBy} onSort={handleSort} filters={filterCampaigns} onFilterChange={setFilterCampaigns} />
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Tab AdGroups */}
          <Tab.Pane eventKey="adgroups">
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white border-bottom py-2">
                <h6 className="mb-0">
                  📂 Grupos de Anuncios ({data?.adGroups?.length || 0})
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <AdGroupsTable data={data?.adGroups} sortBy={sortBy} onSort={handleSort} filters={filterAdGroups} onFilterChange={setFilterAdGroups} />
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Tab Ads */}
          <Tab.Pane eventKey="ads">
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white border-bottom py-2">
                <h6 className="mb-0">
                  📄 Anuncios ({data?.ads?.length || 0})
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                <AdsTable data={data?.ads} sortBy={sortBy} onSort={handleSort} filters={filterAds} onFilterChange={setFilterAds} />
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Tab Plataformas */}
          <Tab.Pane eventKey="platforms">
            <Row className="g-3">
              {data?.byPlatform?.map((platform, idx) => (
                <Col lg={4} key={idx}>
                  <Card className="shadow-sm border-0 h-100">
                    <Card.Header 
                      className="py-2 text-white"
                      style={{ backgroundColor: platformColors[platform.platform] }}
                    >
                      <h6 className="mb-0">{platform.platform}</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Inversión</span>
                        <span className="fw-bold">{formatCurrency(platform.cost)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Impresiones</span>
                        <span>{formatNumber(platform.impressions)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Clicks</span>
                        <span>{formatNumber(platform.clicks)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">CTR</span>
                        <span>{formatPercent(platform.ctr)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Video Views</span>
                        <span>{formatNumber(platform.videoViews)}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Leads</span>
                        <span>{formatNumber(platform.leads)}</span>
                      </div>
                      <hr />
                      <div className="text-center">
                        <Badge bg="light" text="dark" className="px-3 py-2">
                          {platform.percentage}% del total
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Tab.Pane>

          {/* Tab Geográfico */}
          <Tab.Pane eventKey="geographic">
            <GeographicSection data={data?.byRegion} />
          </Tab.Pane>

          {/* Tab Demografía */}
          <Tab.Pane eventKey="demographics">
            <DemographicsSection data={data?.byAge} />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};

// Componente de Sección Geográfica
const GeographicSection = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState('impressions');
  
  const metricLabels = {
    impressions: 'Impresiones',
    clicks: 'Clicks',
    cost: 'Inversión',
    leads: 'Leads',
  };

  if (!data || data.length === 0) {
    return <div className="text-center text-muted py-5">Sin datos geográficos disponibles</div>;
  }

  const chartData = [...data].sort((a, b) => b[selectedMetric] - a[selectedMetric]);
  const maxValue = Math.max(...chartData.map(d => d[selectedMetric]));

  return (
    <Row className="g-3">
      <Col lg={8}>
        <Card className="shadow-sm border-0">
          <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              📍 Métricas por Región/Provincia
            </h6>
            <div className="btn-group btn-group-sm">
              {Object.entries(metricLabels).map(([key, label]) => (
                <Button 
                  key={key}
                  variant={selectedMetric === key ? 'primary' : 'outline-secondary'}
                  onClick={() => setSelectedMetric(key)}
                  size="sm"
                >
                  {label}
                </Button>
              ))}
            </div>
          </Card.Header>
          <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {chartData.map((row, idx) => (
              <div key={idx} className="mb-2">
                <div className="d-flex justify-content-between mb-1">
                  <small className="text-truncate" style={{ maxWidth: '150px' }}>{row.region}</small>
                  <small className="fw-bold">
                    {selectedMetric === 'cost' ? formatCurrency(row[selectedMetric]) : formatNumber(row[selectedMetric])}
                  </small>
                </div>
                <div className="progress" style={{ height: '20px' }}>
                  <div 
                    className="progress-bar" 
                    style={{ 
                      width: `${(row[selectedMetric] / maxValue) * 100}%`,
                      backgroundColor: '#5b9bd5'
                    }}
                  />
                </div>
              </div>
            ))}
          </Card.Body>
        </Card>
      </Col>
      <Col lg={4}>
        <Card className="shadow-sm border-0">
          <Card.Header className="bg-white border-bottom py-2">
            <h6 className="mb-0">Top 10 Regiones</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <Table hover size="sm" className="mb-0" style={{ fontSize: '11px' }}>
              <thead className="bg-light">
                <tr>
                  <th>#</th>
                  <th>Región</th>
                  <th className="text-end">{metricLabels[selectedMetric]}</th>
                </tr>
              </thead>
              <tbody>
                {chartData.slice(0, 10).map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <Badge bg={idx < 3 ? 'primary' : 'secondary'}>{idx + 1}</Badge>
                    </td>
                    <td className="text-truncate" style={{ maxWidth: '150px' }}>{row.region}</td>
                    <td className="text-end fw-bold">
                      {selectedMetric === 'cost' ? formatCurrency(row[selectedMetric]) : formatNumber(row[selectedMetric])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

// Componente de Sección Demografía
const DemographicsSection = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="text-center text-muted py-5">Sin datos demográficos disponibles</div>;
  }

  const totalImpressions = data.reduce((acc, d) => acc + d.impressions, 0);

  // Colores para rangos de edad
  const ageColors = {
    '13-17': '#ff6b6b',
    '18-24': '#feca57',
    '25-34': '#48dbfb',
    '35-44': '#1dd1a1',
    '45-54': '#5f27cd',
    '55-64': '#ff9ff3',
    '65+': '#54a0ff',
  };

  return (
    <Row className="g-3">
      <Col lg={6}>
        <Card className="shadow-sm border-0">
          <Card.Header className="bg-white border-bottom py-2">
            <h6 className="mb-0">
              👥 Distribución por Edad
            </h6>
          </Card.Header>
          <Card.Body>
            {data.map((row, idx) => {
              const percentage = totalImpressions > 0 ? (row.impressions / totalImpressions * 100) : 0;
              return (
                <div key={idx} className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="fw-bold" style={{ color: ageColors[row.ageRange] || '#666' }}>
                      {row.ageRange}
                    </span>
                    <span>{percentage.toFixed(1)}%</span>
                  </div>
                  <div className="progress" style={{ height: '25px' }}>
                    <div 
                      className="progress-bar" 
                      style={{ 
                        width: `${percentage}%`,
                        backgroundColor: ageColors[row.ageRange] || '#5b9bd5'
                      }}
                    >
                      <span className="fw-bold">{formatNumber(row.impressions)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </Card.Body>
        </Card>
      </Col>
      <Col lg={6}>
        <Card className="shadow-sm border-0">
          <Card.Header className="bg-white border-bottom py-2">
            <h6 className="mb-0">
              📋 Detalle por Rango de Edad
            </h6>
          </Card.Header>
          <Card.Body className="p-0">
            <Table hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
              <thead className="bg-light">
                <tr>
                  <th>Edad</th>
                  <th className="text-end">Impresiones</th>
                  <th className="text-end">Clicks</th>
                  <th className="text-end">CTR</th>
                  <th className="text-end">Inversión</th>
                  <th className="text-end">%</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <Badge style={{ backgroundColor: ageColors[row.ageRange] || '#666' }}>
                        {row.ageRange}
                      </Badge>
                    </td>
                    <td className="text-end">{formatNumber(row.impressions)}</td>
                    <td className="text-end">{formatNumber(row.clicks)}</td>
                    <td className="text-end">{formatPercent(row.ctr)}</td>
                    <td className="text-end">{formatCurrency(row.cost)}</td>
                    <td className="text-end">
                      <Badge bg="light" text="dark">{row.percentage}%</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-light fw-bold">
                <tr>
                  <td>Total</td>
                  <td className="text-end">{formatNumber(totalImpressions)}</td>
                  <td className="text-end">{formatNumber(data.reduce((a, d) => a + d.clicks, 0))}</td>
                  <td className="text-end">-</td>
                  <td className="text-end">{formatCurrency(data.reduce((a, d) => a + d.cost, 0))}</td>
                  <td className="text-end">100%</td>
                </tr>
              </tfoot>
            </Table>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default ClaroDashboard;
